import dayjs from 'dayjs';
import capitalize from 'lodash/capitalize';
import isNil from 'lodash/isNil';
import { v4 as uuid } from 'uuid';

import { COUNTRIES } from 'constants/countries';
import { US_STATES } from 'constants/usStates';
import { toCurrency } from 'store/accounts/helpers';
import { toMaintenanceWindowModel } from 'store/common/helpers';
import type { Nullable, Paginate } from 'types';
import { isPaginatedDTO } from 'utils/guards';

import {
  type BulkAutoExtendSettingsDTO,
  type BulkOperationDTO,
  type ChangeAuthenticationTypeDTO,
  type ChangeProtocolDTO,
  type ChangeThreadsDTO,
  type ChangeUplinkSpeedDTO,
  type CredentialsGeneratorDTO,
  type DiagnosticRoutineDTO,
  type IPWhitelistDTO,
  LimitParameterDTO,
  OperationTypeDTO,
  type ProxyActionDTO,
  type ProxyAdminDetailsDTO,
  type ProxyAdminPricesDTO,
  type ProxyAdminServerDTO,
  type ProxyAdminSubnetDTO,
  type ProxyAdminVendorDTO,
  type ProxyAuthenticationDTO,
  type ProxyBandwidthDTO,
  type ProxyBulkOperationDTO,
  type ProxyBulkOperationTaskDTO,
  type ProxyChangelogDTO,
  type ProxyConnectionDTO,
  type ProxyDTO,
  type ProxyEventDTO,
  type ProxyExtensionPriceDTO,
  type ProxyISPDTO,
  type ProxyLimitSignalDTO,
  type ProxyLimitSignalsListItemDTO,
  type ProxyLocationDTO,
  type ProxyOverviewDTO,
  type ProxyReplacementCheckDTO,
  type ProxyReplacementDetailsDTO,
  type ProxyReplacementOptionsDTO,
  type ProxyRouteDTO,
  ProxyStatusChangeFailureReasonDTO,
  ProxyStatusDTO,
  type ProxySubnetDTO,
  type ProxySummaryDTO,
  type ProxyTrafficDataDTO,
  type ProxyTrafficDTO,
  type ProxyUptimeDataDTO,
  type ProxyUptimeDTO,
  type UpgradeThreadsPriceDTO,
  type UpgradeUplinkSpeedPriceDTO,
} from './dtos';
import {
  type BulkAutoExtendSettingsModel,
  type BulkOperationModel,
  type ChangeAuthenticationTypeModel,
  type ChangeProtocolModel,
  type ChangeThreadsModel,
  type ChangeUplinkSpeedModel,
  type CredentialsGeneratorModel,
  type DiagnosticRoutineModel,
  type IPWhitelistModel,
  type ISPOption,
  LimitParameter,
  LimitStatus,
  ProxyActionModel,
  type ProxyAdminDetailsModel,
  type ProxyAdminPricesModel,
  type ProxyAdminServerModel,
  type ProxyAdminSubnetModel,
  type ProxyAdminVendorModel,
  type ProxyAuthenticationModel,
  type ProxyBandwidthModel,
  type ProxyBulkOperationModel,
  type ProxyBulkOperationTaskModel,
  type ProxyChangelogModel,
  type ProxyConnectionModel,
  type ProxyEventModel,
  type ProxyExtensionPriceModel,
  type ProxyISPModel,
  type ProxyLimitModel,
  type ProxyLimitSignalModel,
  type ProxyLocationModel,
  type ProxyModel,
  type ProxyOverviewModel,
  type ProxyReplacementDetailsModel,
  type ProxyReplacementOptionsModel,
  type ProxyRouteModel,
  ProxyStatus,
  ProxyStatusChangeFailureReason,
  type ProxySubnetModel,
  type ProxySummaryModel,
  type ProxyTrafficDataModel,
  type ProxyTrafficModel,
  type ProxyUptimeDataModel,
  type ProxyUptimeModel,
  type UpgradeThreadsPriceModel,
  type UpgradeUplinkSpeedPriceModel,
} from './models';
import { type CountryOption, NetworkType, OperationType, ServiceType } from './types';

// #region Common
export function toServiceType(from: NetworkType): ServiceType;
export function toServiceType(from: NetworkType[]): ServiceType[];

export function toServiceType(from: NetworkType | NetworkType[]): ServiceType | ServiceType[] {
  if (Array.isArray(from)) {
    return from.map((item) => toServiceType(item));
  }

  if (from === NetworkType.Datacenter) return ServiceType.PROXY_DATACENTER;

  if (from === NetworkType.Mobile) return ServiceType.PROXY_MOBILE;

  if (from === NetworkType.Residential) return ServiceType.PROXY_RESIDENTIAL;

  if (from === NetworkType.ResidentialStatic) return ServiceType.PROXY_RESIDENTIAL_STATIC;

  return ServiceType.VPN;
}

export function toNetworkType(from: ServiceType): NetworkType;
export function toNetworkType(from: ServiceType[]): NetworkType[];

export function toNetworkType(from: ServiceType | ServiceType[]): NetworkType | NetworkType[] {
  if (Array.isArray(from)) {
    return from.map((item) => toNetworkType(item));
  }

  if (from === ServiceType.PROXY_DATACENTER) return NetworkType.Datacenter;

  if (from === ServiceType.PROXY_MOBILE) return NetworkType.Mobile;

  if (from === ServiceType.PROXY_RESIDENTIAL) return NetworkType.Residential;

  if (from === ServiceType.PROXY_RESIDENTIAL_STATIC) return NetworkType.ResidentialStatic;

  return NetworkType.VPN;
}

export function toProxyActionModel(from: ProxyActionDTO | ProxyActionModel): ProxyActionModel {
  if (from === ProxyActionModel.SHOW_DETAILS) return ProxyActionModel.SHOW_DETAILS;

  return ProxyActionModel[from];
}

export function toProxyAdminServerModel(from: ProxyAdminServerDTO): ProxyAdminServerModel;
export function toProxyAdminServerModel(from: ProxyAdminServerDTO[]): ProxyAdminServerModel[];

export function toProxyAdminServerModel(
  from: ProxyAdminServerDTO | ProxyAdminServerDTO[],
): ProxyAdminServerModel | ProxyAdminServerModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyAdminServerModel(item));
  }

  return from;
}

export function toProxyAdminSubnetModel(from: ProxyAdminSubnetDTO): ProxyAdminSubnetModel;
export function toProxyAdminSubnetModel(from: ProxyAdminSubnetDTO[]): ProxyAdminSubnetModel[];

export function toProxyAdminSubnetModel(
  from: ProxyAdminSubnetDTO | ProxyAdminSubnetDTO[],
): ProxyAdminSubnetModel | ProxyAdminSubnetModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyAdminSubnetModel(item));
  }

  return from;
}

export function toProxyAdminVendorModel(from: ProxyAdminVendorDTO): ProxyAdminVendorModel;
export function toProxyAdminVendorModel(from: ProxyAdminVendorDTO[]): ProxyAdminVendorModel[];

export function toProxyAdminVendorModel(
  from: ProxyAdminVendorDTO | ProxyAdminVendorDTO[],
): ProxyAdminVendorModel | ProxyAdminVendorModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyAdminVendorModel(item));
  }

  return from;
}

export function toProxyAdminPricesModel(from: ProxyAdminPricesDTO): ProxyAdminPricesModel;
export function toProxyAdminPricesModel(from: ProxyAdminPricesDTO[]): ProxyAdminPricesModel[];

export function toProxyAdminPricesModel(
  from: ProxyAdminPricesDTO | ProxyAdminPricesDTO[],
): ProxyAdminPricesModel | ProxyAdminPricesModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyAdminPricesModel(item));
  }

  return from;
}

export function toProxyAuthenticationModel(from: ProxyAuthenticationDTO): ProxyAuthenticationModel;
export function toProxyAuthenticationModel(from: ProxyAuthenticationDTO[]): ProxyAuthenticationModel[];

export function toProxyAuthenticationModel(
  from: ProxyAuthenticationDTO | ProxyAuthenticationDTO[],
): ProxyAuthenticationModel | ProxyAuthenticationModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyAuthenticationModel(item));
  }

  return from;
}

export function toProxyBandwidthModel(from: ProxyBandwidthDTO): ProxyBandwidthModel;
export function toProxyBandwidthModel(from: ProxyBandwidthDTO[]): ProxyBandwidthModel[];

export function toProxyBandwidthModel(
  from: ProxyBandwidthDTO | ProxyBandwidthDTO[],
): ProxyBandwidthModel | ProxyBandwidthModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyBandwidthModel(item));
  }

  return from;
}

export function toProxyConnectionModel(from: ProxyConnectionDTO): ProxyConnectionModel;
export function toProxyConnectionModel(from: ProxyConnectionDTO[]): ProxyConnectionModel[];

export function toProxyConnectionModel(
  from: ProxyConnectionDTO | ProxyConnectionDTO[],
): ProxyConnectionModel | ProxyConnectionModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyConnectionModel(item));
  }

  const { hostnames, ...rest } = from;

  return { ...rest, hostnames: Array.isArray(hostnames) ? null : hostnames };
}

export function toProxyLocationModel(from: ProxyLocationDTO): ProxyLocationModel;
export function toProxyLocationModel(from: ProxyLocationDTO[]): ProxyLocationModel[];

export function toProxyLocationModel(
  from: ProxyLocationDTO | ProxyLocationDTO[],
): ProxyLocationModel | ProxyLocationModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyLocationModel(item));
  }

  const { countryCode, regionCode } = from;

  const country = COUNTRIES.find((c) => c.value === countryCode) ?? null;
  const region = US_STATES.find((r) => r.value === regionCode) ?? null;

  return { country, region };
}

export function toProxyRouteModel(from: ProxyRouteDTO): ProxyRouteModel;
export function toProxyRouteModel(from: ProxyRouteDTO[]): ProxyRouteModel[];

export function toProxyRouteModel(from: ProxyRouteDTO | ProxyRouteDTO[]): ProxyRouteModel | ProxyRouteModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyRouteModel(item));
  }

  const { proxyService, server, ...rest } = from;

  return { ...rest, proxyId: proxyService, serverId: server };
}

export function toProxyTrafficDataModel(from: ProxyTrafficDataDTO): ProxyTrafficDataModel;
export function toProxyTrafficDataModel(from: ProxyTrafficDataDTO[]): ProxyTrafficDataModel[];

export function toProxyTrafficDataModel(
  from: ProxyTrafficDataDTO | ProxyTrafficDataDTO[],
): ProxyTrafficDataModel | ProxyTrafficDataModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyTrafficDataModel(item));
  }

  const { date, value } = from;

  return { date, value: value * 1024 * 1024 * 1024 };
}

export function toProxyTrafficModel(from: ProxyTrafficDTO): ProxyTrafficModel;
export function toProxyTrafficModel(from: ProxyTrafficDTO[]): ProxyTrafficModel[];

export function toProxyTrafficModel(
  from: ProxyTrafficDTO | ProxyTrafficDTO[],
): ProxyTrafficModel | ProxyTrafficModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyTrafficModel(item));
  }

  const { data, ...rest } = from;

  return { ...rest, data: toProxyTrafficDataModel(data) };
}

export function toProxyUptimeDataModel(from: ProxyUptimeDataDTO): ProxyUptimeDataModel;
export function toProxyUptimeDataModel(from: ProxyUptimeDataDTO[]): ProxyUptimeDataModel[];

export function toProxyUptimeDataModel(
  from: ProxyUptimeDataDTO | ProxyUptimeDataDTO[],
): ProxyUptimeDataModel | ProxyUptimeDataModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyUptimeDataModel(item));
  }

  return from;
}

export function toProxyUptimeModel(from: ProxyUptimeDTO): ProxyUptimeModel;
export function toProxyUptimeModel(from: ProxyUptimeDTO[]): ProxyUptimeModel[];

export function toProxyUptimeModel(from: ProxyUptimeDTO | ProxyUptimeDTO[]): ProxyUptimeModel | ProxyUptimeModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyUptimeModel(item));
  }

  const { data, ...rest } = from;

  return { ...rest, data: toProxyUptimeDataModel(data) };
}

// Thresholds per the BE contract (PD-3555/PD-3556 comments) — API returns raw peak/limit only, no status.
export function toLimitStatus(percentage: number): LimitStatus {
  if (percentage >= 100) return LimitStatus.LIMIT_REACHED;

  if (percentage >= 90) return LimitStatus.NEAR_CAPACITY;

  if (percentage >= 80) return LimitStatus.APPROACHING_LIMIT;

  if (percentage >= 65) return LimitStatus.GROWING_USAGE;

  return LimitStatus.NORMAL;
}

export function toLimitParameter(from: LimitParameterDTO): LimitParameter;
export function toLimitParameter(from: LimitParameterDTO[]): LimitParameter[];

export function toLimitParameter(from: LimitParameterDTO | LimitParameterDTO[]): LimitParameter | LimitParameter[] {
  if (Array.isArray(from)) {
    return from.map((item) => toLimitParameter(item));
  }

  if (from === LimitParameterDTO.SPEED) return LimitParameter.SPEED;

  if (from === LimitParameterDTO.CONCURRENCY) return LimitParameter.CONCURRENCY;

  return LimitParameter.BANDWIDTH;
}

export function toProxyLimitModel(from: ProxyLimitSignalDTO): ProxyLimitModel;
export function toProxyLimitModel(from: ProxyLimitSignalDTO[]): ProxyLimitModel[];

export function toProxyLimitModel(
  from: ProxyLimitSignalDTO | ProxyLimitSignalDTO[],
): ProxyLimitModel | ProxyLimitModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyLimitModel(item));
  }

  const { peak, limit, parameter, hourly, ...rest } = from;
  const percentage = limit > 0 ? (peak / limit) * 100 : 0;

  return {
    ...rest,
    parameter: toLimitParameter(parameter),
    peak,
    limit,
    percentage,
    status: toLimitStatus(percentage),
    hourly: hourly ?? null,
  };
}

// Every signal row is its own card — the API returns at most 3 items, no grouping by proxyId.
export function toProxyLimitSignalModel(from: ProxyLimitSignalsListItemDTO[]): ProxyLimitSignalModel[] {
  return from.map(({ proxyId, signal, ...rest }) => ({
    proxyId,
    ...rest,
    limit: toProxyLimitModel(signal),
  }));
}

export function toProxyStatus(from: ProxyStatusDTO, expiresAt: Nullable<string>): ProxyStatus {
  const threeDaysBefore = dayjs(expiresAt).subtract(3, 'day');

  if (from === ProxyStatusDTO.ACTIVE && dayjs().isBetween(threeDaysBefore, dayjs(expiresAt))) {
    return ProxyStatus.EXPIRING_SOON;
  }

  return ProxyStatus[ProxyStatusDTO[from]];
}
// #endregion Common

export function toBulkAutoExtendSettingsModel(from: BulkAutoExtendSettingsDTO): BulkAutoExtendSettingsModel;
export function toBulkAutoExtendSettingsModel(from: BulkAutoExtendSettingsDTO[]): BulkAutoExtendSettingsModel[];

export function toBulkAutoExtendSettingsModel(
  from: BulkAutoExtendSettingsDTO | BulkAutoExtendSettingsDTO[],
): BulkAutoExtendSettingsModel | BulkAutoExtendSettingsModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toBulkAutoExtendSettingsModel(item));
  }

  return from;
}

export function toProxyExtensionPriceModel(from: ProxyExtensionPriceDTO): ProxyExtensionPriceModel;
export function toProxyExtensionPriceModel(from: ProxyExtensionPriceDTO[]): ProxyExtensionPriceModel[];

export function toProxyExtensionPriceModel(
  from: ProxyExtensionPriceDTO | ProxyExtensionPriceDTO[],
): ProxyExtensionPriceModel | ProxyExtensionPriceModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyExtensionPriceModel(item));
  }

  const { currency, ...rest } = from;

  return { ...rest, currency: currency ? toCurrency(currency) : null };
}

export function toChangeThreadsModel(from: ChangeThreadsDTO): ChangeThreadsModel;
export function toChangeThreadsModel(from: ChangeThreadsDTO[]): ChangeThreadsModel[];

export function toChangeThreadsModel(
  from: ChangeThreadsDTO | ChangeThreadsDTO[],
): ChangeThreadsModel | ChangeThreadsModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toChangeThreadsModel(item));
  }

  const { availableThreads, ...rest } = from;

  return { ...rest, options: availableThreads.map(({ id }) => id) };
}

export function toChangeUplinkSpeedModel(from: ChangeUplinkSpeedDTO): ChangeUplinkSpeedModel;
export function toChangeUplinkSpeedModel(from: ChangeUplinkSpeedDTO[]): ChangeUplinkSpeedModel[];

export function toChangeUplinkSpeedModel(
  from: ChangeUplinkSpeedDTO | ChangeUplinkSpeedDTO[],
): ChangeUplinkSpeedModel | ChangeUplinkSpeedModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toChangeUplinkSpeedModel(item));
  }

  const { availableUplinkSpeeds, ...rest } = from;

  return { ...rest, options: availableUplinkSpeeds.map(({ id }) => id) };
}

export function toIPWhitelistModel(from: IPWhitelistDTO): IPWhitelistModel;
export function toIPWhitelistModel(from: IPWhitelistDTO[]): IPWhitelistModel[];

export function toIPWhitelistModel(from: IPWhitelistDTO | IPWhitelistDTO[]): IPWhitelistModel | IPWhitelistModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toIPWhitelistModel(item));
  }

  return from;
}

export function toCredentialsGeneratorModel(from: CredentialsGeneratorDTO): CredentialsGeneratorModel;
export function toCredentialsGeneratorModel(from: CredentialsGeneratorDTO[]): CredentialsGeneratorModel[];

export function toCredentialsGeneratorModel(
  from: CredentialsGeneratorDTO | CredentialsGeneratorDTO[],
): CredentialsGeneratorModel | CredentialsGeneratorModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toCredentialsGeneratorModel(item));
  }

  const { credentials, locations } = from;

  return {
    credentials,
    locations: {
      countries: Object.keys(locations)
        .map((country) => ({
          value: country.toUpperCase(),
          label: `countriesregions:countries.${country.toUpperCase() === 'UK' ? 'GB' : country.toUpperCase()}`,
        }))
        .filter((v) => !isNil(v.value)),
      states: Object.entries(locations).reduce((acc, [country, state]) => {
        if (!state) return { ...acc, [country.toUpperCase()]: [] };

        return {
          ...acc,
          [country.toUpperCase()]: Object.keys(state).map((s) => ({ value: s, label: capitalize(s) })),
        };
      }, {}),
      cities: Object.entries(locations).reduce((acc, [country, state]) => {
        if (!state) return { ...acc, [country.toUpperCase()]: [] };

        return {
          ...acc,
          [country.toUpperCase()]: Object.entries(state).reduce((curr, [currState, city]) => {
            if (!city) return { ...curr, [currState]: [] };

            return { ...curr, [currState]: city.map((c) => ({ value: c, label: capitalize(c) })) };
          }, {}),
        };
      }, {}),
    },
  };
}

export function toProxyModel(from: ProxyDTO): ProxyModel;
export function toProxyModel(from: ProxyDTO[]): ProxyModel[];
export function toProxyModel(from: Paginate<ProxyDTO>): Paginate<ProxyModel>;

export function toProxyModel(
  from: ProxyDTO | ProxyDTO[] | Paginate<ProxyDTO>,
): ProxyModel | ProxyModel[] | Paginate<ProxyModel> {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyModel(item));
  }

  if (isPaginatedDTO(from)) {
    const { data, ...rest } = from;

    return { ...rest, data: toProxyModel(data) };
  }

  const {
    status,
    authentication,
    connection,
    metadata,
    bandwidth,
    uplinkSpeed,
    threads,
    location,
    actions,
    maintenanceWindows,
    ...rest
  } = from;

  return {
    ...rest,
    status: toProxyStatus(status, from.expiresAt),
    authentication: toProxyAuthenticationModel(authentication),
    connection: toProxyConnectionModel(connection),
    ispName: metadata.ispName,
    bandwidth: toProxyBandwidthModel(bandwidth),
    uplinkSpeed: uplinkSpeed?.value != null ? uplinkSpeed.value : null,
    threads: threads?.value != null ? threads.value : null,
    location: toProxyLocationModel(location),
    actions: [ProxyActionModel.SHOW_DETAILS, ...actions].map(toProxyActionModel),
    maintenanceWindows: toMaintenanceWindowModel(maintenanceWindows),
  };
}

export function toProxyOverviewModel(from: ProxyOverviewDTO): ProxyOverviewModel;
export function toProxyOverviewModel(from: ProxyOverviewDTO[]): ProxyOverviewModel[];
export function toProxyOverviewModel(from: Paginate<ProxyOverviewDTO>): Paginate<ProxyOverviewModel>;

export function toProxyOverviewModel(
  from: ProxyOverviewDTO | ProxyOverviewDTO[] | Paginate<ProxyOverviewDTO>,
): ProxyOverviewModel | ProxyOverviewModel[] | Paginate<ProxyOverviewModel> {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyOverviewModel(item));
  }

  if (isPaginatedDTO(from)) {
    const { data, ...rest } = from;

    return { ...rest, data: toProxyOverviewModel(data) };
  }

  const {
    activatedAt,
    canceledAt,
    updatedAt,
    autoExtendEnabled,
    autoExtendBandwidthAmount,
    autoExtendAt,
    routes,
    traffic,
    uptime,
    type,
    plan,
    currentUsage,
    ...rest
  } = from;

  return {
    ...toProxyModel(rest),
    plan,
    activatedAt,
    canceledAt,
    updatedAt,
    isAutoExtendEnabled: autoExtendEnabled,
    autoExtendBandwidthAmount,
    autoExtendAt,
    routes: toProxyRouteModel(routes),
    traffic: toProxyTrafficModel(traffic),
    uptime: toProxyUptimeModel(uptime),
    type,
    currentUsage: toProxyLimitModel(currentUsage),
  };
}

export function toProxySummaryModel(from: ProxySummaryDTO): ProxySummaryModel;
export function toProxySummaryModel(from: ProxySummaryDTO[]): ProxySummaryModel[];

export function toProxySummaryModel(
  from: ProxySummaryDTO | ProxySummaryDTO[],
): ProxySummaryModel | ProxySummaryModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxySummaryModel(item));
  }

  const { activeProxyCount, expiringProxyCount, inactiveProxyCount } = from;

  return { active: activeProxyCount, expiring: expiringProxyCount, inactive: inactiveProxyCount };
}

export function toProxyISPModel(from: ProxyISPDTO): ProxyISPModel;
export function toProxyISPModel(from: ProxyISPDTO[]): ProxyISPModel[];

export function toProxyISPModel(from: ProxyISPDTO | ProxyISPDTO[]): ProxyISPModel | ProxyISPModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyISPModel(item));
  }

  return from;
}

export function toChangeAuthenticationTypeModel(from: ChangeAuthenticationTypeDTO): ChangeAuthenticationTypeModel;
export function toChangeAuthenticationTypeModel(from: ChangeAuthenticationTypeDTO[]): ChangeAuthenticationTypeModel[];

export function toChangeAuthenticationTypeModel(
  from: ChangeAuthenticationTypeDTO | ChangeAuthenticationTypeDTO[],
): ChangeAuthenticationTypeModel | ChangeAuthenticationTypeModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toChangeAuthenticationTypeModel(item));
  }

  return from;
}

export function toProxyChangelogModel(from: ProxyChangelogDTO): ProxyChangelogModel;
export function toProxyChangelogModel(from: ProxyChangelogDTO[]): ProxyChangelogModel[];

export function toProxyChangelogModel(
  from: ProxyChangelogDTO | ProxyChangelogDTO[],
): ProxyChangelogModel | ProxyChangelogModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyChangelogModel(item));
  }

  return { ...from, id: uuid() };
}

export function toProxyEventModel(from: ProxyEventDTO): ProxyEventModel;
export function toProxyEventModel(from: ProxyEventDTO[]): ProxyEventModel[];

export function toProxyEventModel(from: ProxyEventDTO | ProxyEventDTO[]): ProxyEventModel | ProxyEventModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyEventModel(item));
  }

  return { ...from, id: uuid() };
}

export function toProxyAdminDetailsModel(from: ProxyAdminDetailsDTO): ProxyAdminDetailsModel;
export function toProxyAdminDetailsModel(from: ProxyAdminDetailsDTO[]): ProxyAdminDetailsModel[];

export function toProxyAdminDetailsModel(
  from: ProxyAdminDetailsDTO | ProxyAdminDetailsDTO[],
): ProxyAdminDetailsModel | ProxyAdminDetailsModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyAdminDetailsModel(item));
  }

  const { server, subnet, vendor, prices, routes, status, statusChangeFailureReason, ...rest } = from;

  return {
    ...rest,
    userStatus: status,
    server: server ? toProxyAdminServerModel(server) : null,
    subnet: subnet ? toProxyAdminSubnetModel(subnet) : null,
    vendor: vendor ? toProxyAdminVendorModel(vendor) : [],
    prices: prices ? toProxyAdminPricesModel(prices) : null,
    routes: toProxyRouteModel(routes),
    statusChangeFailureReason: statusChangeFailureReason
      ? ProxyStatusChangeFailureReason[ProxyStatusChangeFailureReasonDTO[statusChangeFailureReason]]
      : null,
  };
}

export function toProxySubnetModel(from: ProxySubnetDTO): ProxySubnetModel;
export function toProxySubnetModel(from: ProxySubnetDTO[]): ProxySubnetModel[];

export function toProxySubnetModel(from: ProxySubnetDTO | ProxySubnetDTO[]): ProxySubnetModel | ProxySubnetModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxySubnetModel(item));
  }

  const { cidr, countryCode, id, serverName } = from;

  return { countryCode, id, label: `${cidr} (${serverName})` };
}

export function toChangeProtocolModel(from: ChangeProtocolDTO): ChangeProtocolModel;
export function toChangeProtocolModel(from: ChangeProtocolDTO[]): ChangeProtocolModel[];

export function toChangeProtocolModel(
  from: ChangeProtocolDTO | ChangeProtocolDTO[],
): ChangeProtocolModel | ChangeProtocolModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toChangeProtocolModel(item));
  }

  return from;
}

export function toUpgradeThreadsPriceModel(from: UpgradeThreadsPriceDTO): UpgradeThreadsPriceModel[] {
  const { availableThreads } = from;

  return availableThreads.map(({ id, totalPrice }) => ({ price: Number(totalPrice), thread: id }));
}

export function toUpgradeUplinkSpeedPriceModel(from: UpgradeUplinkSpeedPriceDTO): UpgradeUplinkSpeedPriceModel[] {
  const { availableUplinkSpeeds } = from;

  return availableUplinkSpeeds.map(({ id, totalPrice }) => ({ price: Number(totalPrice), mbit: id }));
}

export function toDiagnosticRoutineModel(from: DiagnosticRoutineDTO): DiagnosticRoutineModel;
export function toDiagnosticRoutineModel(from: DiagnosticRoutineDTO[]): DiagnosticRoutineModel[];

export function toDiagnosticRoutineModel(
  from: DiagnosticRoutineDTO | DiagnosticRoutineDTO[],
): DiagnosticRoutineModel | DiagnosticRoutineModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toDiagnosticRoutineModel(item));
  }

  return from;
}

export function toProxyReplacementCheckModel(from: ProxyReplacementCheckDTO): boolean {
  return from.isReplacementAvailable;
}

export function toProxyReplacementDetailsModel(from: ProxyReplacementDetailsDTO): ProxyReplacementDetailsModel;
export function toProxyReplacementDetailsModel(from: ProxyReplacementDetailsDTO[]): ProxyReplacementDetailsModel[];

export function toProxyReplacementDetailsModel(
  from: ProxyReplacementDetailsDTO | ProxyReplacementDetailsDTO[],
): ProxyReplacementDetailsModel | ProxyReplacementDetailsModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyReplacementDetailsModel(item));
  }

  const { status, ...rest } = from;

  return { ...rest, status: toProxyStatus(status, from.expiresAt) };
}

function toSupportedCountriesModel(
  from: Array<{ code: string; amount: Nullable<string> }>,
  isUnused?: boolean,
): CountryOption[] {
  return from
    .map(({ amount, code }) => {
      const country = COUNTRIES.find((c) => c.value === code);

      if (!country) return null;

      return { ...country, subLabel: amount ?? 'common:labels.outOfStock', plainAmount: amount, isUnused };
    })
    .filter((val) => !isNil(val)) as CountryOption[];
}

function toSupportedISPsModel(
  from: [] | Record<string, [] | Record<string, { name: string; amount: Nullable<string> }>>,
): ISPOption[] {
  if (Array.isArray(from)) return [];

  return Object.entries(from)
    .filter(([, data]) => !Array.isArray(data))
    .map(([country, data]) => {
      return Object.entries(data).map(([id, { amount, name }]) => ({
        id,
        country,
        name,
        amount: amount ?? 'common:labels.outOfStock',
      }));
    })
    .flat();
}

export function toProxyReplacementOptionsModel(
  from: ProxyReplacementOptionsDTO & { networkType: NetworkType },
): ProxyReplacementOptionsModel {
  return {
    [from.networkType]: {
      [from.isUnused ? 'premium' : 'standard']: {
        country: toSupportedCountriesModel(from.supportedCountries, from.isUnused),
        isps: toSupportedISPsModel(from.supportedISPs),
      },
    },
  };
}

export function toBulkOperationModel(from: BulkOperationDTO): BulkOperationModel {
  return from;
}

export function toOperationType(from: OperationTypeDTO): OperationType {
  if (from === OperationTypeDTO.PERIOD_EXTENSION) {
    return OperationType.ExtendPeriod;
  }

  return OperationType.ExtendBandwidth;
}

export function toProxyBulkOperationModel({ type, ...from }: ProxyBulkOperationDTO): ProxyBulkOperationModel {
  return { ...from, type: toOperationType(type) };
}

export function toProxyBulkOperationTaskModel(from: ProxyBulkOperationTaskDTO): ProxyBulkOperationTaskModel;
export function toProxyBulkOperationTaskModel(from: ProxyBulkOperationTaskDTO[]): ProxyBulkOperationTaskModel[];

export function toProxyBulkOperationTaskModel(
  from: ProxyBulkOperationTaskDTO | ProxyBulkOperationTaskDTO[],
): ProxyBulkOperationTaskModel | ProxyBulkOperationTaskModel[] {
  if (Array.isArray(from)) {
    return from.map((item) => toProxyBulkOperationTaskModel(item));
  }

  const {
    details: { trafficLimit, ...restDetails },
    ...rest
  } = from;

  return { ...restDetails, traffic: trafficLimit, ...rest };
}
